<template>
  <div
    v-if="!armour_locations || armour_locations.length === 0"
    class="container-fluid m-3 mb-4"
  >
    <p class="bb-no-records mt-3 p-2">No armour locations available</p>
  </div>
  <div
    v-else
    class="bb-armour-grid-wrapper"
  >
    <div class="bb-armour-grid">
      <div
        ref="gridContent"
        class="bb-armour-grid-content"
      >
        <svg
          v-if="backConnectors.length > 0"
          class="bb-armour-grid-overlay"
          :viewBox="measuredOverlayViewBox"
          preserveAspectRatio="none"
        >
          <template
            v-for="connector in backConnectors"
            :key="connector.id"
          >
            <line
              v-if="connector.type === 'line'"
              class="bb-armour-connector"
              :stroke="connector.stroke || null"
              :stroke-linecap="connector.strokeLinecap || null"
              :stroke-width="connector.strokeWidth"
              :x1="connector.x1"
              :y1="connector.y1"
              :x2="connector.x2"
              :y2="connector.y2"
            />
            <path
              v-else
              class="bb-armour-connector"
              :stroke="connector.stroke || null"
              :stroke-width="connector.strokeWidth"
              :d="connector.d"
              fill="none"
            />
          </template>
        </svg>
        <svg
          v-if="measuredConnectors.length > 0"
          class="bb-armour-grid-overlay bb-armour-grid-overlay-measured"
          :viewBox="measuredOverlayViewBox"
          preserveAspectRatio="none"
        >
          <template
            v-for="connector in measuredConnectors"
            :key="connector.id"
          >
            <path
              class="bb-armour-connector"
              :stroke-width="connector.strokeWidth"
              :d="connector.d"
              fill="none"
            />
          </template>
        </svg>
        <div
          v-for="(row, rowIndex) in armourGrid"
          :key="rowIndex"
          class="bb-armour-grid-row"
          :class="{ 'bb-armour-grid-row-top': rowIndex === 0 }"
        >
          <div
            v-for="(cell, colIndex) in row"
            :key="colIndex"
            class="bb-armour-grid-cell"
            :class="{
              'bb-armour-grid-cell-fatigue': rowIndex === 0 && colIndex === 1,
              'bb-armour-grid-cell-fatigue-levels':
                rowIndex === 0 && colIndex === 4,
            }"
            :data-row-index="rowIndex"
            :data-col-index="colIndex"
            :data-location-name="cell?.armour_location_name || ''"
          >
            <div
              v-if="rowIndex === 0 && colIndex === 1"
              class="bb-hitlocations-fatigue-field"
            >
              <div class="form-group bb-hitlocations-fatigue-stack">
                <div class="form-floating mb-2">
                  <input
                    id="calculated-fatigue"
                    class="form-control"
                    :value="calculatedFatigueLevel"
                    readonly
                    disabled
                    type="text"
                  />
                  <label
                    class="optional bb-float-label"
                    for="calculated-fatigue"
                    >Calculated Fatigue Level</label
                  >
                </div>
                <div class="form-floating">
                  <template v-if="lockCombatState">
                    <input
                      id="fatigue-override"
                      class="form-control"
                      :value="local_character?.fatigue_override || ''"
                      readonly
                      disabled
                      type="text"
                    />
                    <label
                      class="optional bb-float-label"
                      for="fatigue-override"
                      >Override Fatigue Level</label
                    >
                  </template>
                  <template v-else>
                    <v-select
                      :options="overrideFatigueLevels"
                      class="form-control"
                      placeholder="..."
                      :modelValue="local_character?.fatigue_override || null"
                      :clearable="true"
                      :disabled="lockCombatState"
                      @update:modelValue="
                        $emit('update-fatigue-override', $event || null)
                      "
                      id="fatigue-override"
                    />
                    <label
                      class="optional bb-float-label"
                      for="fatigue-override"
                      >Override Fatigue Level</label
                    >
                  </template>
                </div>
              </div>
            </div>
            <FatigueLevelsPopup
              v-else-if="rowIndex === 0 && colIndex === 4"
              :base-con="currentConForFatigueTable"
            />
            <CombatArmourCard
              v-else-if="cell"
              :al="cell"
              :body-location-hp="getBodyLocationHp(cell.armour_location_name)"
              :counter-max="getCounterMax(cell)"
              :counter-min="counterMin"
              :readonly-hp-cur="lockCombatState"
              @update-hp-cur="updateHpCur"
            />
            <div
              v-else
              class="bb-armour-grid-filler"
            ></div>
          </div>
        </div>
        <svg
          v-if="frontConnectors.length > 0"
          class="bb-armour-grid-overlay bb-armour-grid-overlay-front"
          :viewBox="measuredOverlayViewBox"
          preserveAspectRatio="none"
        >
          <template
            v-for="connector in frontConnectors"
            :key="connector.id"
          >
            <line
              v-if="connector.type === 'line'"
              class="bb-armour-connector"
              :stroke="connector.stroke || null"
              :stroke-linecap="connector.strokeLinecap || null"
              :stroke-width="connector.strokeWidth"
              :x1="connector.x1"
              :y1="connector.y1"
              :x2="connector.x2"
              :y2="connector.y2"
            />
            <path
              v-else
              class="bb-armour-connector"
              :stroke="connector.stroke || null"
              :stroke-width="connector.strokeWidth"
              :d="connector.d"
              fill="none"
            />
          </template>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import CombatArmourCard from '../components/CombatArmourCard.vue'
import FatigueLevelsPopup from '../components/FatigueLevelsPopup.vue'

// import store from '../store/index'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import calcLocationHPs from '../helpers/calcLocationHPs'
import getFatigueDetails from '../helpers/getFatigueDetails'

import { mapGetters } from 'vuex'
import { inject } from 'vue'

export default {
  name: 'SheetHitLocations',
  setup() {
    const global = inject('global')
    return { global }
  },
  components: {
    CombatArmourCard,
    FatigueLevelsPopup,
  },
  //========================================
  // props
  //------
  props: {
    character: {
      type: Object,
      required: true,
    },
    lockCombatState: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update-fatigue-override', 'update-hp-cur'],
  //========================================
  // data
  //------
  data() {
    return {
      armour_locations: [],
      gridResizeObserver: null,
      lowerLeftMeasuredConnector: null,
      lowerRightMeasuredConnector: null,
      measuredOverlaySize: {
        width: 0,
        height: 0,
      },
    }
  },
  //========================================
  // watch
  //------
  watch: {
    character: {
      handler(newChar) {
        if (!newChar) return
        this.loadArmourLocations()
        this.$nextTick(() => {
          this.updateMeasuredConnectors()
        })
        this.local_character = copyObjectNoRef(newChar)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================
  // computed
  //----------
  computed: {
    resolvedSizCur() {
      const sizCur = Number(this.character?.siz_cur)
      if (!Number.isNaN(sizCur) && sizCur > 0) {
        return sizCur
      }

      const characteristic = Array.isArray(this.character?.characteristics)
        ? this.character.characteristics.find((entry) => entry?.name === 'siz')
        : null
      const fallbackSizCur = Number(characteristic?.cur)
      return Number.isNaN(fallbackSizCur) ? 0 : fallbackSizCur
    },
    resolvedConCur() {
      const conCur = Number(this.character?.con_cur)
      if (!Number.isNaN(conCur) && conCur > 0) {
        return conCur
      }

      const characteristic = Array.isArray(this.character?.characteristics)
        ? this.character.characteristics.find((entry) => entry?.name === 'con')
        : null
      const fallbackConCur = Number(characteristic?.cur)
      return Number.isNaN(fallbackConCur) ? 0 : fallbackConCur
    },
    fatigueLevels() {
      const lookups = this.getCharacterLookups()
      const fatigueLevels = lookups.fatigue ? lookups.fatigue : []
      return fatigueLevels
    },
    currentConForFatigueTable() {
      const conCur = this.resolvedConCur
      const conWounds = Number(this.character?.con_wounds_tot) || 0
      return conCur + conWounds
    },
    overrideFatigueLevels() {
      return this.fatigueLevels.filter(
        (level) => level !== this.calculatedFatigueLevel,
      )
    },
    calculatedFatigueLevel() {
      const details = getFatigueDetails({
        con_cur: this.resolvedConCur,
        con_wounds: Number(this.character?.con_wounds_tot) || 0,
      })
      return details.level || 'Fresh'
    },
    locationHPs() {
      return calcLocationHPs({
        body_type: this.character?.body_type,
        siz: this.resolvedSizCur,
        con: this.resolvedConCur,
        con_wounds: this.character?.con_wounds_tot,
      })
    },
    counterMin() {
      const conCur = this.resolvedConCur
      const conWounds = Number(this.character?.con_wounds_tot) || 0
      return -(conCur + conWounds)
    },
    armourGrid() {
      const grid = []
      const rowCount = 3
      const columnCount = 5

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        grid[rowIndex] = []
        for (let colIndex = 0; colIndex < columnCount; colIndex++) {
          grid[rowIndex][colIndex] = null
        }
      }

      const placedIds = new Set()
      const hasEquipped = (loc) => {
        return (
          Array.isArray(loc.equipped_armour) && loc.equipped_armour.length > 0
        )
      }

      const isUpperOrLower = (loc) => {
        if (!loc || !loc.armour_location_name) return false
        const name = loc.armour_location_name.toLowerCase()
        return name.startsWith('upper ') || name.startsWith('lower ')
      }

      const getByName = (name) => {
        return (
          this.armour_locations.find(
            (loc) =>
              loc.armour_location_name &&
              loc.armour_location_name.toLowerCase() === name.toLowerCase(),
          ) || null
        )
      }

      const placeByName = (name, row, col, requireEquipped = false) => {
        const match = this.armour_locations.find(
          (loc) =>
            loc.armour_location_name &&
            loc.armour_location_name.toLowerCase() === name.toLowerCase() &&
            (!requireEquipped || hasEquipped(loc)),
        )
        if (match) {
          grid[row][col] = match
          placedIds.add(match.id)
        }
      }

      const placeComposite = ({ base, upper, lower, row, col }) => {
        const baseLoc = base ? getByName(base) : null
        const upperLoc = upper ? getByName(upper) : null
        const lowerLoc = lower ? getByName(lower) : null
        const limbSegments = [upperLoc, lowerLoc].filter(
          (loc) => loc && hasEquipped(loc),
        )

        limbSegments.forEach((loc) => {
          placedIds.add(loc.id)
        })

        if (baseLoc) {
          grid[row][col] = {
            ...baseLoc,
            limb_segments: limbSegments,
          }
          placedIds.add(baseLoc.id)
          return
        }

        const candidates = []
        if (upperLoc && hasEquipped(upperLoc)) candidates.push(upperLoc)
        if (lowerLoc && hasEquipped(lowerLoc)) candidates.push(lowerLoc)

        if (candidates.length > 0) {
          const chosen = candidates.reduce((best, loc) => {
            const ap = loc.highest_ap || 0
            if (!best) return loc
            return ap > (best.highest_ap || 0) ? loc : best
          }, null)

          if (chosen) {
            grid[row][col] = {
              ...chosen,
              limb_segments: limbSegments.filter((loc) => loc.id !== chosen.id),
            }
            placedIds.add(chosen.id)
          }
        }
      }

      placeByName('Head', 0, 2)
      placeByName('Chest', 1, 2)
      placeByName('Abdomen', 2, 2)

      placeComposite({
        base: 'Left Arm',
        upper: 'Upper Left Arm',
        lower: 'Lower Left Arm',
        row: 1,
        col: 1,
      })

      placeComposite({
        base: 'Right Arm',
        upper: 'Upper Right Arm',
        lower: 'Lower Right Arm',
        row: 1,
        col: 3,
      })

      placeComposite({
        base: 'Left Leg',
        upper: 'Upper Left Leg',
        lower: 'Lower Left Leg',
        row: 2,
        col: 1,
      })

      placeComposite({
        base: 'Right Leg',
        upper: 'Upper Right Leg',
        lower: 'Lower Right Leg',
        row: 2,
        col: 3,
      })

      let fillIndex = 0
      for (const loc of this.armour_locations) {
        if (placedIds.has(loc.id)) continue
        if (!hasEquipped(loc) && isUpperOrLower(loc)) continue

        while (fillIndex < rowCount * columnCount) {
          const row = Math.floor(fillIndex / columnCount)
          const col = fillIndex % columnCount
          fillIndex += 1
          if (!grid[row][col]) {
            grid[row][col] = loc
            placedIds.add(loc.id)
            break
          }
        }
      }

      return grid
    },
    locationGridPositions() {
      const positions = new Map()

      this.armourGrid.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (!cell?.armour_location_name) return
          positions.set(cell.armour_location_name, {
            x: colIndex * 20 + 10,
            y: rowIndex * 20 + 10,
          })
        })
      })

      return positions
    },
    connectors() {
      if (
        !this.measuredOverlaySize?.width ||
        !this.measuredOverlaySize?.height
      ) {
        return []
      }

      const legRowYOffset = 0

      return [
        this.buildVerticalConnectorByCell('head-chest', 0, 2, 1, 2),
        this.buildVerticalConnectorByCellWithExtension(
          'chest-abdomen',
          1,
          2,
          2,
          2,
          0,
          3.2,
        ),
        this.buildHorizontalConnectorByCell('chest-left-arm', 1, 1, 1, 2),
        this.buildHorizontalConnectorByCell('chest-right-arm', 1, 2, 1, 3),
        this.buildHorizontalConnectorByCellWithYOffset(
          'abdomen-left-leg',
          2,
          1,
          2,
          2,
          legRowYOffset,
        ),
        this.buildHorizontalConnectorByCellWithYOffset(
          'abdomen-right-leg',
          2,
          2,
          2,
          3,
          legRowYOffset,
        ),
        this.buildLeftElbowToRightMidConnectorByCell(
          'upper-left-arm',
          1,
          1,
          0,
          0,
        ),
        this.buildHorizontalConnectorByCell('lower-left-arm', 1, 0, 1, 1),
        this.buildRightElbowConnectorByCell('upper-right-arm', 1, 3, 0, 4),
        this.buildHorizontalConnectorByCell('lower-right-arm', 1, 3, 1, 4),
        this.buildHorizontalConnectorByCellWithYOffset(
          'upper-left-leg',
          2,
          0,
          2,
          1,
          legRowYOffset,
        ),
        this.buildHorizontalConnectorByCellWithYOffset(
          'upper-right-leg',
          2,
          3,
          2,
          4,
          legRowYOffset,
        ),
      ].filter(Boolean)
    },
    backConnectors() {
      return this.connectors.filter((connector) => !connector.front)
    },
    frontConnectors() {
      return this.connectors.filter((connector) => connector.front)
    },
    measuredConnectors() {
      return [
        this.lowerLeftMeasuredConnector,
        this.lowerRightMeasuredConnector,
      ].filter(Boolean)
    },
    measuredOverlayViewBox() {
      const width = this.measuredOverlaySize?.width || 0
      const height = this.measuredOverlaySize?.height || 0
      return `0 0 ${width} ${height}`
    },
  },
  //========================================
  // methods
  //---------
  methods: {
    ...mapGetters('characters', {
      getCharacterLookups: 'getCharacterLookups',
    }),
    getGridCellLocationName(rowIndex, colIndex) {
      return (
        this.armourGrid?.[rowIndex]?.[colIndex]?.armour_location_name || null
      )
    },
    setupGridResizeObserver() {
      const gridContent = this.$refs.gridContent
      if (!gridContent || typeof ResizeObserver === 'undefined') return

      if (this.gridResizeObserver) {
        this.gridResizeObserver.disconnect()
      }

      this.gridResizeObserver = new ResizeObserver(() => {
        this.updateMeasuredConnectors()
      })
      this.gridResizeObserver.observe(gridContent)
    },
    getGridCardElement(rowIndex, colIndex) {
      const gridContent = this.$refs.gridContent
      if (!gridContent) return null

      return gridContent.querySelector(
        `[data-row-index="${rowIndex}"][data-col-index="${colIndex}"] .armour-card`,
      )
    },
    getGridContentRect() {
      const gridContent = this.$refs.gridContent
      return gridContent ? gridContent.getBoundingClientRect() : null
    },
    scaleOverlayX(value) {
      return (this.measuredOverlaySize.width * value) / 100
    },
    scaleOverlayY(value) {
      return (this.measuredOverlaySize.height * value) / 100
    },
    getRelativeCardRect(rowIndex, colIndex) {
      const card = this.getGridCardElement(rowIndex, colIndex)
      const contentRect = this.getGridContentRect()

      if (!card || !contentRect) return null

      const cardRect = card.getBoundingClientRect()

      return {
        left: cardRect.left - contentRect.left,
        right: cardRect.right - contentRect.left,
        top: cardRect.top - contentRect.top,
        bottom: cardRect.bottom - contentRect.top,
        width: cardRect.width,
        height: cardRect.height,
      }
    },
    getMeasuredCellAnchor(rowIndex, colIndex, side) {
      const rect = this.getRelativeCardRect(rowIndex, colIndex)
      if (!rect) return null

      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      switch (side) {
        case 'left':
          return { x: rect.left, y: centerY }
        case 'right':
          return { x: rect.right, y: centerY }
        case 'top':
          return { x: centerX, y: rect.top }
        case 'bottom':
          return { x: centerX, y: rect.bottom }
        default:
          return { x: centerX, y: centerY }
      }
    },
    getMeasuredCellCenterPoint(rowIndex, colIndex) {
      return this.getMeasuredCellAnchor(rowIndex, colIndex, 'center')
    },
    updateMeasuredConnectors() {
      const gridContent = this.$refs.gridContent
      const leftFromCard = this.getGridCardElement(2, 1)
      const leftToCard = this.getGridCardElement(3, 0)
      const rightFromCard = this.getGridCardElement(2, 3)
      const rightToCard = this.getGridCardElement(3, 4)

      if (!gridContent) {
        this.lowerLeftMeasuredConnector = null
        this.lowerRightMeasuredConnector = null
        return
      }

      const contentRect = gridContent.getBoundingClientRect()

      this.measuredOverlaySize = {
        width: contentRect.width,
        height: contentRect.height,
      }

      if (leftFromCard && leftToCard) {
        const fromRect = leftFromCard.getBoundingClientRect()
        const toRect = leftToCard.getBoundingClientRect()
        const fromX = fromRect.left - contentRect.left + fromRect.width / 2
        const fromY = fromRect.bottom - contentRect.top
        const toX = toRect.right - contentRect.left
        const toY = toRect.top - contentRect.top + toRect.height / 2

        this.lowerLeftMeasuredConnector = {
          id: 'lower-left-leg-measured',
          strokeWidth: 18,
          d: `M ${fromX} ${fromY} L ${fromX} ${toY} L ${toX} ${toY}`,
        }
      } else {
        this.lowerLeftMeasuredConnector = null
      }

      if (rightFromCard && rightToCard) {
        const fromRect = rightFromCard.getBoundingClientRect()
        const toRect = rightToCard.getBoundingClientRect()
        const fromX = fromRect.left - contentRect.left + fromRect.width / 2
        const fromY = fromRect.bottom - contentRect.top
        const toX = toRect.left - contentRect.left
        const toY = toRect.top - contentRect.top + toRect.height / 2

        this.lowerRightMeasuredConnector = {
          id: 'lower-right-leg-measured',
          strokeWidth: 18,
          d: `M ${fromX} ${fromY} L ${fromX} ${toY} L ${toX} ${toY}`,
        }
      } else {
        this.lowerRightMeasuredConnector = null
      }
    },
    getCellAnchor(rowIndex, colIndex, side) {
      const cell = this.armourGrid?.[rowIndex]?.[colIndex]
      if (!cell) return null

      const point = {
        x: colIndex * 20 + 10,
        y: rowIndex * 20 + 10,
      }

      const offsets = {
        left: { x: -2.8, y: 5.2 },
        right: { x: 2.8, y: 5.2 },
        top: { x: 0, y: -4.2 },
        bottom: { x: 0, y: 4.2 },
      }

      const offset = offsets[side]
      if (!offset) return point

      return {
        x: point.x + offset.x,
        y: point.y + offset.y,
      }
    },
    getCellSideMidAnchor(rowIndex, colIndex, side) {
      const cell = this.armourGrid?.[rowIndex]?.[colIndex]
      if (!cell) return null

      const point = {
        x: colIndex * 20 + 10,
        y: rowIndex * 20 + 10,
      }

      const offsets = {
        left: { x: -5.2, y: 0 },
        right: { x: 5.2, y: 0 },
      }

      const offset = offsets[side]
      if (!offset) return point

      return {
        x: point.x + offset.x,
        y: point.y + offset.y,
      }
    },
    buildHorizontalConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getMeasuredCellAnchor(fromRow, fromCol, 'right')
      const toPoint = this.getMeasuredCellAnchor(toRow, toCol, 'left')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
      }
    },
    buildHorizontalConnectorByCellWithYOffset(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      yOffset,
    ) {
      const fromRect = this.getRelativeCardRect(fromRow, fromCol)
      const toRect = this.getRelativeCardRect(toRow, toCol)

      if (!fromRect || !toRect) return null

      const yOffsetPx = this.scaleOverlayY(yOffset)

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromRect.right,
        y1: fromRect.top + fromRect.height / 2 + yOffsetPx,
        x2: toRect.left,
        y2: toRect.top + toRect.height / 2 + yOffsetPx,
      }
    },
    buildVerticalConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getMeasuredCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getMeasuredCellAnchor(toRow, toCol, 'top')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
      }
    },
    buildVerticalConnectorByCellWithExtension(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      startExtension = 0,
      endExtension = 0,
    ) {
      const fromPoint = this.getMeasuredCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getMeasuredCellAnchor(toRow, toCol, 'top')

      if (!fromPoint || !toPoint) return null

      const startExtensionPx = this.scaleOverlayY(startExtension)
      const endExtensionPx = this.scaleOverlayY(endExtension)

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        strokeLinecap: 'butt',
        x1: fromPoint.x,
        y1: fromPoint.y - startExtensionPx,
        x2: toPoint.x,
        y2: toPoint.y + endExtensionPx,
      }
    },
    buildOffsetVerticalConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      xOffset = 0,
      front = false,
    ) {
      const fromPoint = this.getCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getCellAnchor(toRow, toCol, 'top')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        front,
        strokeWidth: 18,
        x1: fromPoint.x + xOffset,
        y1: fromPoint.y,
        x2: toPoint.x + xOffset,
        y2: toPoint.y,
      }
    },
    buildGapOnlyVerticalConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      front = false,
    ) {
      const fromPoint = this.getCellCenterPoint(fromRow, fromCol)
      const toPoint = this.getCellCenterPoint(toRow, toCol)

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        front,
        strokeWidth: 18,
        strokeLinecap: 'butt',
        x1: fromPoint.x,
        y1: fromPoint.y + 10.5,
        x2: toPoint.x,
        y2: toPoint.y - 10.5,
      }
    },
    getCellCenterPoint(rowIndex, colIndex) {
      const cell = this.armourGrid?.[rowIndex]?.[colIndex]
      if (!cell) return null

      return {
        x: colIndex * 20 + 10,
        y: rowIndex * 20 + 10,
      }
    },
    buildLeftElbowConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getCellAnchor(fromRow, fromCol, 'top')
      const toPoint = this.getCellAnchor(toRow, toCol, 'bottom')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildLeftLegRowElbowConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      yOffset,
    ) {
      const fromCenter = this.getCellCenterPoint(fromRow, fromCol)
      const toCenter = this.getCellCenterPoint(toRow, toCol)

      if (!fromCenter || !toCenter) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromCenter.x} ${fromCenter.y - 4.2} L ${fromCenter.x} ${toCenter.y + yOffset} L ${toCenter.x} ${toCenter.y + yOffset}`,
      }
    },
    buildLeftElbowToRightMidConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
    ) {
      const fromPoint = this.getMeasuredCellAnchor(fromRow, fromCol, 'top')
      const toPoint = this.getMeasuredCellAnchor(toRow, toCol, 'right')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildDownLeftElbowConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getCellSideMidAnchor(toRow, toCol, 'right')

      if (!fromPoint || !toPoint) return null

      const adjustedToPoint = {
        x: toPoint.x - 2.4,
        y: toPoint.y,
      }

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${adjustedToPoint.y} L ${adjustedToPoint.x} ${adjustedToPoint.y}`,
      }
    },
    buildDownRightElbowConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getCellSideMidAnchor(toRow, toCol, 'left')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildRightElbowConnectorByCell(id, fromRow, fromCol, toRow, toCol) {
      const fromPoint = this.getMeasuredCellAnchor(fromRow, fromCol, 'top')
      const toPoint = this.getMeasuredCellAnchor(toRow, toCol, 'left')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildRightLegRowElbowConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      yOffset,
    ) {
      const fromCenter = this.getCellCenterPoint(fromRow, fromCol)
      const toCenter = this.getCellCenterPoint(toRow, toCol)

      if (!fromCenter || !toCenter) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromCenter.x} ${fromCenter.y - 4.2} L ${fromCenter.x} ${toCenter.y + yOffset} L ${toCenter.x} ${toCenter.y + yOffset}`,
      }
    },
    buildLeftDetourConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      detour = 8,
    ) {
      const fromPoint = this.getCellAnchor(fromRow, fromCol, 'bottom')
      const toPoint = this.getCellAnchor(toRow, toCol, 'top')

      if (!fromPoint || !toPoint) return null

      const detourX = Math.min(fromPoint.x, toPoint.x) - detour

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${detourX} ${fromPoint.y} L ${detourX} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildGutterVerticalConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      side = 'left',
      gutterOffset = 10,
    ) {
      const fromSide = side === 'right' ? 'right' : 'left'
      const toSide = side === 'right' ? 'right' : 'left'
      const fromPoint = this.getCellAnchor(fromRow, fromCol, fromSide)
      const toPoint = this.getCellAnchor(toRow, toCol, toSide)

      if (!fromPoint || !toPoint) return null

      const gutterX =
        side === 'right'
          ? Math.max(fromPoint.x, toPoint.x) + gutterOffset
          : Math.min(fromPoint.x, toPoint.x) - gutterOffset

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${gutterX} ${fromPoint.y} L ${gutterX} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildSideVerticalConnectorByCell(
      id,
      fromRow,
      fromCol,
      toRow,
      toCol,
      side = 'left',
      sideOffset = null,
    ) {
      const anchorSide = side === 'right' ? 'right' : 'left'
      const resolvedOffset =
        sideOffset == null ? (anchorSide === 'right' ? 5.2 : -5.2) : sideOffset
      const signedOffset =
        anchorSide === 'right' ? resolvedOffset : -Math.abs(resolvedOffset)
      const fromPoint = this.getCustomCellSideMidAnchor(
        fromRow,
        fromCol,
        anchorSide,
        signedOffset,
      )
      const toPoint = this.getCustomCellSideMidAnchor(
        toRow,
        toCol,
        anchorSide,
        signedOffset,
      )

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
      }
    },
    getCustomCellSideMidAnchor(rowIndex, colIndex, side, xOffset) {
      const cell = this.armourGrid?.[rowIndex]?.[colIndex]
      if (!cell) return null

      const point = {
        x: colIndex * 20 + 10,
        y: rowIndex * 20 + 10,
      }

      if (side !== 'left' && side !== 'right') return point

      return {
        x: point.x + xOffset,
        y: point.y,
      }
    },
    getLocationAnchor(locationName, side) {
      const point = this.locationGridPositions.get(locationName)
      if (!point) return null

      const offsets = {
        left: { x: -2.8, y: 5.2 },
        right: { x: 2.8, y: 5.2 },
        top: { x: 0, y: -4.2 },
        bottom: { x: 0, y: 4.2 },
      }

      const offset = offsets[side]
      if (!offset) return point

      return {
        x: point.x + offset.x,
        y: point.y + offset.y,
      }
    },
    buildHorizontalConnector(id, fromName, toName) {
      const fromPoint = this.getLocationAnchor(fromName, 'right')
      const toPoint = this.getLocationAnchor(toName, 'left')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
      }
    },
    buildVerticalConnector(id, fromName, toName) {
      const fromPoint = this.getLocationAnchor(fromName, 'bottom')
      const toPoint = this.getLocationAnchor(toName, 'top')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x,
        y1: fromPoint.y,
        x2: toPoint.x,
        y2: toPoint.y,
      }
    },
    buildShiftedVerticalConnector(id, fromName, toName, xOffset = 0) {
      const fromPoint = this.getLocationAnchor(fromName, 'bottom')
      const toPoint = this.getLocationAnchor(toName, 'top')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'line',
        strokeWidth: 18,
        x1: fromPoint.x + xOffset,
        y1: fromPoint.y,
        x2: toPoint.x + xOffset,
        y2: toPoint.y,
      }
    },
    buildLeftDetourConnector(id, fromName, toName, detour = 8) {
      const fromPoint = this.getLocationAnchor(fromName, 'bottom')
      const toPoint = this.getLocationAnchor(toName, 'top')

      if (!fromPoint || !toPoint) return null

      const detourX = Math.min(fromPoint.x, toPoint.x) - detour

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${detourX} ${fromPoint.y} L ${detourX} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildLeftElbowConnector(id, fromName, toName) {
      const fromPoint = this.getLocationAnchor(fromName, 'top')
      const toPoint = this.getLocationAnchor(toName, 'bottom')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    buildRightElbowConnector(id, fromName, toName) {
      const fromPoint = this.getLocationAnchor(fromName, 'top')
      const toPoint = this.getLocationAnchor(toName, 'bottom')

      if (!fromPoint || !toPoint) return null

      return {
        id,
        type: 'path',
        strokeWidth: 18,
        d: `M ${fromPoint.x} ${fromPoint.y} L ${fromPoint.x} ${toPoint.y} L ${toPoint.x} ${toPoint.y}`,
      }
    },
    getBodyLocationHp(locationName) {
      if (!locationName) return ''

      const normalizedName = locationName.toLowerCase()

      if (normalizedName === 'head') return this.locationHPs.head || ''
      if (normalizedName === 'chest') return this.locationHPs.chest || ''
      if (normalizedName === 'abdomen') return this.locationHPs.abdomen || ''
      if (normalizedName === 'left arm' || normalizedName === 'right arm') {
        return this.locationHPs.arms || ''
      }
      if (normalizedName === 'left leg' || normalizedName === 'right leg') {
        return this.locationHPs.legs || ''
      }

      return ''
    },
    getCounterMax(location) {
      const hpTot = Number(location?.hp_tot)
      if (!Number.isNaN(hpTot)) {
        return hpTot
      }

      const bodyLocationHp = Number(
        this.getBodyLocationHp(location?.armour_location_name),
      )

      return Number.isNaN(bodyLocationHp) ? 0 : bodyLocationHp
    },
    updateHpCur(change) {
      this.$emit('update-hp-cur', change)
    },
    loadArmourLocations() {
      console.log('-------------------------------')
      console.log('IN loadArmourLocations')
      this.armour_locations = this.character.armour_locations

      if (this.armour_locations) {
        this.armour_locations.sort((a, b) => {
          return a.seq - b.seq
        })
        // console.log(
        //   ' this.armour_locations (pre-sync) = ',
        //   window.$deepCopy(this.armour_locations),
        // )

        try {
          const items = Array.isArray(this.character.items)
            ? this.character.items
            : []
          if (items.length) {
            const itemsById = new Map()
            items.forEach((it) => {
              if (it && it.id != null) {
                itemsById.set(it.id, it)
              }
            })

            this.armour_locations.forEach((loc) => {
              if (!loc || !Array.isArray(loc.equipped_armour)) return

              const derivedBodyLocationHp = Number(
                this.getBodyLocationHp(loc.armour_location_name),
              )
              if (
                !Number.isNaN(derivedBodyLocationHp) &&
                derivedBodyLocationHp > 0
              ) {
                const currentHpTot = Number(loc.hp_tot)
                if (Number.isNaN(currentHpTot) || currentHpTot <= 0) {
                  loc.hp_tot = derivedBodyLocationHp
                }

                const currentHpCur = Number(loc.hp_cur)
                if (
                  Number.isNaN(currentHpCur) ||
                  (currentHpCur <= 0 &&
                    (Number.isNaN(currentHpTot) || currentHpTot <= 0))
                ) {
                  loc.hp_cur = derivedBodyLocationHp
                }
              }

              const equipped = loc.equipped_armour
              let armour_count = 0
              let highest_ap = 0
              let highest_layer = 0
              let dex_pen_total = 0

              equipped.forEach((ea) => {
                if (!ea || ea.armour == null) return

                const item = itemsById.get(ea.armour)
                if (!item) return

                ea.ap = item.ap ?? ea.ap ?? 0
                ea.hp = item.hp ?? ea.hp ?? null

                const dexPenFactor = Number(ea.dex_pen_factor)
                const apVal = Number(ea.ap) || 0
                const itemDexPen = Number(item.dex_pen)
                let perLocationDex = Number(ea.dex_pen)

                if (Number.isNaN(perLocationDex)) {
                  perLocationDex = 0
                }

                if (!Number.isNaN(dexPenFactor) && dexPenFactor >= 0) {
                  perLocationDex = apVal * dexPenFactor
                } else if (!Number.isNaN(itemDexPen) && itemDexPen >= 0) {
                  perLocationDex = Number(ea.dex_pen)
                }

                if (loc.half_location) {
                  perLocationDex = perLocationDex / 2
                }

                ea.dex_pen = perLocationDex
                armour_count += 1

                if (apVal > highest_ap) highest_ap = apVal

                const layerVal = Number(ea.layer)
                if (!Number.isNaN(layerVal) && layerVal > highest_layer) {
                  highest_layer = layerVal
                }

                dex_pen_total += perLocationDex
              })

              loc.armour_count = armour_count
              loc.highest_ap = highest_ap
              loc.highest_layer = highest_layer
              loc.dex_pen = dex_pen_total
            })
          }
        } catch (e) {
          console.warn(
            'Unable to sync armour_locations from equipped items:',
            e,
          )
        }

        // console.log(
        //   ' this.armour_locations (post-sync) = ',
        //   window.$deepCopy(this.armour_locations),
        // )
      }
    },
  },
  //========================================
  // mounted
  //---------
  mounted() {
    this.$nextTick(() => {
      this.updateMeasuredConnectors()
      this.setupGridResizeObserver()
    })
  },
  //========================================
  // beforeUnmount
  //---------------
  beforeUnmount() {
    if (this.gridResizeObserver) {
      this.gridResizeObserver.disconnect()
      this.gridResizeObserver = null
    }
  },
}
</script>

<style scoped>
.armour-image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
}

.armour-image {
  max-height: 100%;
  max-width: 100%;
  width: auto;
  object-fit: contain;
  display: block;
}

.bb-armour-grid-wrapper {
  position: relative;
  margin-top: 0;
  padding-left: 0;
}

.bb-hitlocations-fatigue-field {
  width: 100%;
  max-width: 100%;
  align-self: flex-start;
  transform: translate(5rem, 2.7rem);
}

.bb-hitlocations-fatigue-stack {
  display: flex;
  flex-direction: column;
}

.bb-hitlocations-fatigue-stack {
  display: flex;
  flex-direction: column;
}

.bb-armour-grid-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -2.4rem;
  width: 3px;
  background-color: transparent;
  pointer-events: none;
}

.bb-armour-grid {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.bb-armour-grid-content {
  position: relative;
  display: flex;
  flex-direction: column;
  --bb-armour-row-gap: 0.9rem;
  gap: var(--bb-armour-row-gap);
  width: 100%;
}

.bb-armour-grid-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: visible;
}

.bb-armour-grid-overlay-front {
  z-index: 2;
}

.bb-armour-connector {
  stroke: #3c3c3c;
  stroke-linecap: square;
  stroke-linejoin: round;
  opacity: 1;
  vector-effect: non-scaling-stroke;
}

.bb-armour-grid-row {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns:
    minmax(0, 0.03fr) minmax(0, 2.2fr) minmax(0, 2.2fr)
    minmax(0, 2.2fr) minmax(0, 0.03fr);
  gap: 0.75rem;
}

.bb-armour-grid-row-top {
  z-index: 6;
}

.bb-armour-grid-cell {
  min-height: 4rem;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  overflow: visible;
}

.bb-armour-grid-cell-fatigue {
  justify-content: flex-start;
}

.bb-armour-grid-cell-fatigue-levels {
  justify-content: flex-end;
  align-items: flex-start;
}

.bb-armour-grid-filler {
  width: 100%;
}

.bb-hitlocations-fatigue-field {
  position: relative;
  z-index: 30;
  width: 60%;
  align-self: flex-start;
}

.bb-hitlocations-fatigue-field :deep(.vs__dropdown-toggle),
.bb-hitlocations-fatigue-field :deep(.vs__selected-options),
.bb-hitlocations-fatigue-field :deep(.vs__actions) {
  position: relative;
  z-index: 5;
}

.bb-hitlocations-fatigue-field :deep(.vs__dropdown-menu) {
  z-index: 20;
}
</style>
